import { useMemo } from 'react'
import { HeatmapChart } from '../../charts/HeatmapChart'
import { formatMoney } from '../../format'
import { useWidgetData } from '../../widgets/useWidgetData'
import { WidgetFrame } from '../../widgets/WidgetFrame'
import { WIDGET_KEY, type WidgetProps } from '../../widgets/types'
import { runExport, type ExportColumn } from '../../../../lib/export'
import type { CreditValueByJurisdictionResult } from '../../../../types/insights'
import { buildJurisdictionHeatmapGrid, type JurisdictionHeatmapGrid } from './analyticsMappers'

const WIDGET_KEY_SELF = WIDGET_KEY.creditValueByJurisdictionFy
/** Every certified-value fact at launch is USD (mirrors `CreditValueByJurisdictionFyWidget`'s own disclosed constant, AC2 footnote). */
const REPORTING_CURRENCY = 'USD'

interface HeatmapExportRow {
  jurisdiction: string
  fiscalYearId: string
  amount: number
}

function flatten(grid: JurisdictionHeatmapGrid): HeatmapExportRow[] {
  return grid.cells.map((c) => ({ jurisdiction: c.rowKey, fiscalYearId: c.colKey, amount: c.value }))
}

const HEATMAP_EXPORT_COLUMNS: ExportColumn<HeatmapExportRow>[] = [
  { id: 'jurisdiction', header: 'Jurisdiction', toText: (r) => r.jurisdiction },
  { id: 'fiscalYearId', header: 'Fiscal year', toText: (r) => r.fiscalYearId },
  { id: 'amount', header: `Certified amount (${REPORTING_CURRENCY})`, toText: (r) => formatMoney(String(r.amount), REPORTING_CURRENCY) },
]

/**
 * IB-282 View 3, AC3.1 — "Credit value by state × FY" heatmap. Reuses the ALREADY-shipped
 * `credit-value-by-jurisdiction-fy` widget key (zero new backend read, per the Tech Lead design
 * §2) — only the reshape (`buildJurisdictionHeatmapGrid`) and the chart type (`HeatmapChart`, new)
 * are new. A heatmap cell click cross-filters `jurisdiction` (AC3.5's first drill level).
 */
export function JurisdictionHeatmapWidget({ filter, skipRefetch, onCrossFilter }: WidgetProps) {
  const { status, data, message, retry } = useWidgetData<CreditValueByJurisdictionResult>(WIDGET_KEY_SELF, filter, skipRefetch)

  const buckets = useMemo(() => (Array.isArray(data?.buckets) ? data.buckets : []), [data])
  const grid = useMemo(() => buildJurisdictionHeatmapGrid(buckets), [buckets])

  const frameState = status === 'ok' && grid.rows.length === 0 ? 'empty' : status

  return (
    <WidgetFrame
      title="Credit value by state × FY"
      icon="map-pin"
      state={frameState}
      message={message}
      onRetry={retry}
      exportAction={{
        onClick: () => runExport('csv', 'Credit value by state-FY', flatten(grid), HEATMAP_EXPORT_COLUMNS),
      }}
    >
      <HeatmapChart
        title="Credit value by state × FY"
        rows={grid.rows}
        columns={grid.columns}
        cells={grid.cells}
        valueFormatter={(v) => formatMoney(String(v), REPORTING_CURRENCY)}
        onCellClick={(rowKey) => onCrossFilter('jurisdiction', rowKey)}
      />
    </WidgetFrame>
  )
}
